import React, { createContext, useState, useEffect, ReactNode } from 'react'
import { authenticateWithGoogle } from '../services/authService'
import { authApi } from '../api/auth.api'
import type { AuthResponse, EmailLoginRequest, EmailRegisterRequest, LinkGoogleRequest, UpdateProfileRequest } from '../types/auth'

interface User {
  id: string
  userId: string
  email: string
  name: string
  userName: string
  picture?: string
  memberLevel: number
  isRegister: boolean
  lastLoginAt: string
  authProvider: string
  phone?: string
}

interface LinkingState {
  linkToken: string
  email: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (credential: string) => Promise<void>
  logout: () => void
  registerWithEmail: (request: EmailRegisterRequest) => Promise<void>
  loginWithEmail: (request: EmailLoginRequest) => Promise<void>
  updateProfile: (request: UpdateProfileRequest) => Promise<void>
  linkingPending: LinkingState | null
  confirmLinkAccount: (password: string) => Promise<void>
  dismissLinking: () => void
  isAuthenticated: boolean
  isLoading: boolean
  hasEverLoggedIn: boolean
  lastUserInfo: User | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_KEY = 'cloutyspace_auth_token'
const USER_KEY = 'cloutyspace_user'
const LOGIN_HISTORY_KEY = 'cloutyspace_login_history'
const LAST_USER_KEY = 'cloutyspace_last_user'

interface AuthProviderProps {
  children: ReactNode
}

function mapAuthResponseToUser(response: AuthResponse): User {
  return {
    id: response.userId,
    userId: response.userId,
    email: response.email,
    name: response.name,
    userName: response.userName,
    picture: response.picture,
    memberLevel: response.memberLevel,
    isRegister: response.isRegister,
    lastLoginAt: response.lastLoginAt,
    authProvider: response.authProvider ?? 'google',
    phone: response.phoneNumber,
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasEverLoggedIn, setHasEverLoggedIn] = useState(false)
  const [lastUserInfo, setLastUserInfo] = useState<User | null>(null)
  const [linkingPending, setLinkingPending] = useState<LinkingState | null>(null)

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedUser = localStorage.getItem(USER_KEY)
    const loginHistory = localStorage.getItem(LOGIN_HISTORY_KEY)
    const lastUser = localStorage.getItem(LAST_USER_KEY)

    if (loginHistory === 'true') {
      setHasEverLoggedIn(true)
    }

    if (lastUser) {
      try {
        setLastUserInfo(JSON.parse(lastUser))
      } catch (error) {
        console.error('Error parsing last user info:', error)
      }
    }

    if (storedToken && storedUser) {
      try {
        setToken(storedToken)
        setUser(JSON.parse(storedUser))
      } catch (error) {
        console.error('Error loading stored auth data:', error)
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
      }
    }
    setIsLoading(false)
  }, [])

  const persistSession = (userData: User, accessToken: string) => {
    setUser(userData)
    setToken(accessToken)
    setHasEverLoggedIn(true)
    setLastUserInfo(userData)
    localStorage.setItem(TOKEN_KEY, accessToken)
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
    localStorage.setItem(LOGIN_HISTORY_KEY, 'true')
    localStorage.setItem(LAST_USER_KEY, JSON.stringify(userData))
  }

  const login = async (credential: string) => {
    console.log('🔐 開始 Google 登入驗證...')
    const response: AuthResponse = await authenticateWithGoogle(credential)
    console.log('✅ 後端認證成功:', response)

    if (response.requiresLinking && response.linkToken) {
      // 需要帳號連結：先暫存 linkToken
      setLinkingPending({ linkToken: response.linkToken, email: response.email })
      return
    }

    persistSession(mapAuthResponseToUser(response), response.accessToken)
    console.log('✅ Google 登入成功')
  }

  const registerWithEmail = async (request: EmailRegisterRequest) => {
    const response = await authApi.registerWithEmail(request)
    persistSession(mapAuthResponseToUser(response), response.accessToken)
  }

  const loginWithEmail = async (request: EmailLoginRequest) => {
    const response = await authApi.loginWithEmail(request)
    persistSession(mapAuthResponseToUser(response), response.accessToken)
  }

  const updateProfile = async (request: UpdateProfileRequest) => {
    if (!token) throw new Error('Not authenticated')
    const updated = await authApi.updateProfile(token, request)
    const updatedUser: User = {
      ...user!,
      name: updated.displayName,
      userName: updated.userName,
      phone: updated.phoneNumber,
    }
    setUser(updatedUser)
    setLastUserInfo(updatedUser)
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser))
    localStorage.setItem(LAST_USER_KEY, JSON.stringify(updatedUser))
  }

  const confirmLinkAccount = async (password: string) => {
    if (!linkingPending) return
    const request: LinkGoogleRequest = { linkToken: linkingPending.linkToken, password }
    const response = await authApi.linkGoogle(request)
    setLinkingPending(null)
    persistSession(mapAuthResponseToUser(response), response.accessToken)
  }

  const dismissLinking = () => setLinkingPending(null)

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    console.log('用戶已登出')
  }

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    registerWithEmail,
    loginWithEmail,
    updateProfile,
    linkingPending,
    confirmLinkAccount,
    dismissLinking,
    isAuthenticated: !!user && !!token,
    isLoading,
    hasEverLoggedIn,
    lastUserInfo,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
